import React, { useCallback, useEffect, useState } from 'react';
import { regenerateDay } from '../../services/itinerary/regenerationApi';
import { DEFAULT_REGENERATION_SUGGESTIONS } from '../../config/regenerationSuggestions';

export default function DayRegenerationSheet({
  isOpen,
  onClose,
  onStart,
  onSuccess,
  onError,
  itineraryId,
  interviewId,
  versionId,
  dayIndex,
  dayTitle,
  editToken,
  regenerationsRemaining = 20,
  maxRegenerations = 20,
  isPreviewMode = false,
  previewReady = true
}) {
  const [instruction, setInstruction] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const previewBlocked = isPreviewMode && !previewReady;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setInstruction('');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setInstruction('');
    setError(null);
    setSubmitting(false);
  }, [dayIndex, isOpen]);

  const handleChipClick = useCallback((chipInstruction) => {
    setInstruction(prev => {
      // Append to existing instruction with proper spacing
      if (!prev.trim()) {
        return chipInstruction;
      }
      // Add space if previous doesn't end with punctuation
      const needsSpace = !/[.!?]\s*$/.test(prev.trim());
      return prev.trim() + (needsSpace ? ' ' : '') + chipInstruction;
    });
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (typeof dayIndex !== 'number') {
      setError('Invalid day selection');
      return;
    }

    const trimmedInstruction = instruction.trim();
    if (!trimmedInstruction) {
      setError('Please provide regeneration instructions');
      return;
    }

    if (previewBlocked) {
      setError('Your preview is still finishing Day 1. Please try again in a moment.');
      return;
    }

    if (!isPreviewMode && !editToken) {
      setError('Edit token is missing. This itinerary may be view-only.');
      return;
    }

    setError(null);
    setSubmitting(true);
    const resolvedDayTitle = dayTitle || `Day ${dayIndex + 1}`;
    onStart?.({ dayIndex, dayTitle: resolvedDayTitle });

    try {
      const result = await regenerateDay({
        itineraryId,
        interviewId,
        versionId,
        dayIndex,
        instruction: trimmedInstruction,
        editToken
      });
      onSuccess?.(result);
      onClose?.();
    } catch (err) {
      console.error('[DayRegenerationSheet] Regeneration failed', err);
      const fallbackMessage = 'Failed to regenerate day. Please try again.';
      const detailsMessage = err?.details?.message || err?.details?.error;
      const nextMessage = err?.message || detailsMessage || fallbackMessage;
      setError(nextMessage);
      onError?.(err);
    } finally {
      setSubmitting(false);
    }
  }, [instruction, previewBlocked, editToken, isPreviewMode, itineraryId, interviewId, versionId, dayIndex, dayTitle, onStart, onSuccess, onClose, onError]);

  if (!isOpen) return null;
  if (typeof dayIndex !== 'number') return null;
  const remainingLabel = isPreviewMode ? 'preview regenerations' : 'regenerations';
  const isSubmitDisabled = submitting || !instruction.trim() || previewBlocked;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg rounded-t-lg bg-slate-900 shadow-2xl sm:rounded-lg">
        {/* Header */}
        <div className="border-b border-white/10 px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-white">
                Regenerate: {dayTitle || `Day ${dayIndex + 1}`}
              </h2>
              <p className="mt-1 text-sm text-emerald-300">
                {regenerationsRemaining} of {maxRegenerations} {remainingLabel} remaining
              </p>
              {previewBlocked && (
                <p className="mt-2 text-xs text-amber-200">
                  Preview is still finishing Day 1. You can draft your changes now, then regenerate once it&apos;s ready.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="instruction" className="mb-2 block text-sm font-semibold text-white">
                What would you like to change?
              </label>
              <textarea
                id="instruction"
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-white/20 bg-slate-800/60 px-4 py-3 text-white placeholder-white/40 shadow-inner focus:border-emerald-400/60 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
                placeholder="e.g., I want more beach time and less driving"
                disabled={submitting}
              />
            </div>

            {/* Suggestion Chips */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/60">
                Quick suggestions:
              </p>
              <div className="flex flex-wrap gap-2">
                {DEFAULT_REGENERATION_SUGGESTIONS.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    onClick={() => handleChipClick(chip.instruction)}
                    className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1.5 text-sm font-medium text-emerald-300 transition-colors hover:border-emerald-400/50 hover:bg-emerald-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                    disabled={submitting}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary w-full sm:w-auto"
                disabled={false}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary-cta w-full sm:w-auto"
                disabled={isSubmitDisabled}
              >
                Regenerate Day
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
